import { DEFAULT_REQUEST_OPTIONS } from '@uncinc/react-searchkit';
import type { RequestOptions, SearchRequest } from 'searchkit';

type AfterSearch = NonNullable<NonNullable<RequestOptions['hooks']>['afterSearch']>;
export type SearchResponses = Parameters<AfterSearch>[1];

export class SearchRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SearchRequestError';
  }
}

/**
 * Searchkit catches failed search requests (non-2xx status, a body that isn't
 * JSON, network errors) itself and returns an empty list of responses instead
 * of throwing. Without this check, that empty list is cached and rendered as if
 * nothing was found.
 *
 * Throwing here keeps the failed result out of the client cache, so a retry
 * really sends a new request, and puts InstantSearch in its `error` status,
 * which `Search/ErrorState` renders.
 */
export const assertSearchSucceeded = (requests: SearchRequest[], responses: SearchResponses) => {
  if (!Array.isArray(responses) || responses.length < requests.length) {
    throw new SearchRequestError('Search request failed: no response from the search proxy.');
  }

  // Elasticsearch reports per-query failures inside a 200 _msearch response.
  if (responses.some((response) => response && 'error' in response)) {
    throw new SearchRequestError('Search request failed: Elasticsearch returned an error.');
  }
};

/**
 * Add the search error check to the given request options. Falls back to the
 * library defaults, which is what SearchkitWrapper uses without requestOptions.
 */
export const withSearchErrorHandling = (
  requestOptions: RequestOptions = DEFAULT_REQUEST_OPTIONS,
): RequestOptions => ({
  ...requestOptions,
  hooks: {
    ...requestOptions.hooks,
    afterSearch: (requests, responses) => {
      assertSearchSucceeded(requests, responses);

      const afterSearch = requestOptions.hooks?.afterSearch;

      return afterSearch ? afterSearch(requests, responses) : Promise.resolve(responses);
    },
  },
});
